import { PAYMENT_CONFIG } from "@/constants/status";
import type { TaxInvoice } from "@/types";
import { formatSAR } from "@/utils/format";

interface InvoicesTableProps {
  invoices: TaxInvoice[];
  onSelect: (invoice: TaxInvoice) => void;
  onPrint: (invoice: TaxInvoice) => void;
}

export default function InvoicesTable({
  invoices,
  onSelect,
  onPrint,
}: InvoicesTableProps) {
  if (invoices.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-3">
          <i className="ri-file-shield-2-line text-2xl"></i>
        </div>
        <p className="text-sm text-foreground-500">
          لا توجد فواتير مطابقة للبحث الحالي
        </p>
      </div>
    );
  }

  return (
    <div>
      <p className="md:hidden px-5 pt-4 flex items-center gap-1.5 text-xs text-foreground-500">
        <span className="w-4 h-4 flex items-center justify-center text-primary-500">
          <i className="ri-drag-move-2-line"></i>
        </span>
        اسحب الجدول أفقيًا لعرض بقية الأعمدة
      </p>
      <div className="relative">
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-10 z-10 md:hidden bg-gradient-to-r from-white via-white/70 to-transparent"></div>
        <div className="overflow-x-auto pb-1">
      <table className="w-full min-w-[900px] text-right border-collapse">
        <thead>
          <tr className="text-xs text-foreground-500 bg-background-50">
            <th className="font-medium px-5 py-3 whitespace-nowrap">
              رقم الفاتورة
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">التاريخ</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">الفرع</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">العميل</th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              طريقة الدفع
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              إجمالي المنتجات شامل الضريبة
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              الضريبة (15%)
            </th>
            <th className="font-medium px-3 py-3 whitespace-nowrap">
              الإجمالي
            </th>
            <th className="font-medium px-5 py-3 whitespace-nowrap">إجراءات</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const payment = PAYMENT_CONFIG[invoice.payment];
            return (
              <tr
                key={invoice.id}
                className="border-t border-background-100 hover:bg-primary-50/60 transition-colors duration-200"
              >
                <td className="px-5 py-3.5">
                  <span className="text-sm font-bold text-foreground-950" dir="ltr">
                    {invoice.id}
                  </span>
                  <p className="text-xs text-foreground-400 mt-0.5">
                    {invoice.invoiceType === "standard"
                      ? "ضريبية"
                      : "ضريبية مبسطة"}
                  </p>
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-500 whitespace-nowrap">
                  {invoice.date}
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-700 whitespace-nowrap">
                  {invoice.branch}
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-700 whitespace-nowrap">
                  {invoice.customer}
                </td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex items-center gap-1.5 text-sm text-foreground-600 whitespace-nowrap">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                      <i className={payment.icon}></i>
                    </span>
                    {payment.label}
                  </span>
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-600 whitespace-nowrap">
                  {formatSAR(invoice.subtotal)}
                </td>
                <td className="px-3 py-3.5 text-sm text-foreground-600 whitespace-nowrap">
                  {formatSAR(invoice.vat)}
                </td>
                <td className="px-3 py-3.5 text-sm font-semibold text-foreground-900 whitespace-nowrap">
                  {formatSAR(invoice.total)}
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onSelect(invoice)}
                      title="عرض الفاتورة"
                      aria-label="عرض الفاتورة"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => onPrint(invoice)}
                      title="تنزيل PDF"
                      aria-label="تنزيل PDF"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-file-download-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => onPrint(invoice)}
                      title="طباعة"
                      aria-label="طباعة"
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-500 hover:text-white transition-colors duration-200 cursor-pointer"
                    >
                      <i className="ri-printer-line"></i>
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
        </div>
      </div>
    </div>
  );
}